import { PropsWithChildren, useLayoutEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { AsyncContentWrapper } from 'partner360/components/asyncContentWrapper'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from 'partner360/features/financialCockpit/configs/translations'
import { selectFinancialCockpitDownload } from 'partner360/features/financialCockpit/model/selectors'

import styles from './PlanningCockpitContentWrapper.module.scss'

interface IPlanningCockpitContentWrapperProps extends PropsWithChildren {
  isBlurContent: boolean
  isAdditionalLoading?: boolean
  reloadData?: () => void
}

export const PlanningCockpitContentWrapper = ({
  isBlurContent,
  isAdditionalLoading = false,
  children,
  reloadData,
}: IPlanningCockpitContentWrapperProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const download = useSelector(selectFinancialCockpitDownload)
  const combinedDownload = useMemo(
    () =>
      isAdditionalLoading && (download.status === 'idle' || download.status === 'cancelled')
        ? { ...download, status: 'requesting' as const }
        : download,
    [download, isAdditionalLoading]
  )

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const updateLoaderOffset = () => {
      const top = Math.max(0, container.getBoundingClientRect().top + window.scrollY)
      container.style.setProperty('--cockpit-loader-offset', `${top}px`)
    }

    updateLoaderOffset()
    const observer = new ResizeObserver(updateLoaderOffset)
    observer.observe(document.body)
    window.addEventListener('resize', updateLoaderOffset)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateLoaderOffset)
    }
  }, [])

  return (
    <div ref={containerRef}>
      <AsyncContentWrapper
        download={combinedDownload}
        isBlurContent={isBlurContent}
        onRetry={reloadData}
        classes={{ container: styles.loaderContainer }}
        texts={FINANCIAL_COCKPIT_TRANSLATION_KEYS.downloadProgress}
      >
        {children}
      </AsyncContentWrapper>
    </div>
  )
}
